/**
 * AdminActivity — view and search the audit trail.
 *
 * Answers "who did what, when, from where". Every row is something a person did:
 * a login, a photo uploaded or deleted, a line item billed or stripped, an invoice
 * generated or emailed, a dealer assignment changed, a rate edited.
 *
 * ⚠️ HISTORY STARTS 2026-09-11 FOR ANYTHING BILLING-RELATED. The billing router had no
 * audit calls at all before that date, so the absence of older money actions is a gap in
 * what was ever recorded, not a gap in what is shown. Media uploads and logins go back
 * much further. Said plainly on screen so nobody reads an empty result as "nothing
 * happened".
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';

const WINDOWS = [
  { h: 24, label: 'Last 24 hours' },
  { h: 168, label: 'Last 7 days' },
  { h: 720, label: 'Last 30 days' },
  { h: 8760, label: 'Last year' },
];

// Actions worth colouring: money and authority read differently from routine capture.
const TONE = (action) => {
  if (!action) return '#6b7280';
  if (action.startsWith('billing.') || action.startsWith('labor_code.')) return '#9a3412';
  if (action.startsWith('assignments.')) return '#9a3412';
  if (action.includes('delete')) return '#b91c1c';
  if (action.startsWith('visit.')) return '#1d4ed8';
  if (action === 'login') return '#6b7280';
  return '#374151';
};

export default function AdminActivity() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const [q, setQ] = useState('');
  const [action, setAction] = useState('');
  const [user, setUser] = useState('');
  const [hours, setHours] = useState(168);
  const [offset, setOffset] = useState(0);
  const LIMIT = 200;

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('adminAuth');
    if (!token) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const load = useCallback(async () => {
    const headers = authHeader();
    if (!headers) return;
    setLoading(true); setErr(null);
    try {
      const p = new URLSearchParams({ since_hours: hours, limit: LIMIT, offset });
      if (q) p.set('q', q);
      if (action) p.set('action', action);
      if (user) p.set('user', user);
      const r = await fetch(`${API_BASE}/admin/audit-log?${p}`, { headers });
      if (r.status === 401 || r.status === 403) {
        localStorage.removeItem('adminAuth'); navigate('/admin/login'); return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setRows(d.rows || []); setTotal(d.total || 0);
      setActions(d.actions || []); setUsers(d.users || []);
    } catch (e) {
      // A failed load must never look like "nothing happened" — that reads as an
      // all-clear and is the opposite of the truth.
      setErr(e.message || 'Failed to load');
      setRows([]);
    } finally { setLoading(false); }
  }, [authHeader, navigate, q, action, user, hours, offset]);

  useEffect(() => { load(); }, [load]);

  // Debounce the free-text box so typing a VIN doesn't fire a query per keystroke.
  const [qDraft, setQDraft] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setOffset(0); setQ(qDraft); }, 350);
    return () => clearTimeout(t);
  }, [qDraft]);

  const fmt = (ts) => {
    if (!ts) return '';
    const d = new Date(ts + (ts.endsWith('Z') ? '' : 'Z'));
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400 }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24 }}>Activity Log</h1>
      <p style={{ margin: '0 0 18px', color: '#6b7280', fontSize: 14 }}>
        Who did what, when, and from where. Billing and dealer-assignment actions have
        been recorded since <strong>11 Sep 2026</strong>; logins and media go back further.
      </p>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <input
          value={qDraft}
          onChange={(e) => setQDraft(e.target.value)}
          placeholder="Search VIN, email, invoice, dealer, action…"
          style={{ flex: '1 1 320px', padding: '9px 12px', border: '1px solid #d1d5db',
                   borderRadius: 6, fontSize: 14 }}
        />
        <select value={action} onChange={(e) => { setOffset(0); setAction(e.target.value); }}
                style={{ padding: '9px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}>
          <option value="">All actions</option>
          {actions.map(a => <option key={a.action} value={a.action}>{a.action} ({a.n})</option>)}
        </select>
        <select value={user} onChange={(e) => { setOffset(0); setUser(e.target.value); }}
                style={{ padding: '9px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}>
          <option value="">All users</option>
          {users.map(u => <option key={u.who} value={u.who}>{u.who} ({u.n})</option>)}
        </select>
        <select value={hours} onChange={(e) => { setOffset(0); setHours(Number(e.target.value)); }}
                style={{ padding: '9px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}>
          {WINDOWS.map(w => <option key={w.h} value={w.h}>{w.label}</option>)}
        </select>
        <button onClick={load} style={{ padding: '9px 16px', border: '1px solid #d1d5db',
                borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Refresh</button>
      </div>

      {err && (
        <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca',
                      borderRadius: 6, color: '#991b1b', marginBottom: 12 }}>
          Couldn’t load the log: {err}. This is a load failure, not an empty log.
        </div>
      )}

      <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 8 }}>
        {loading ? 'Loading…' : `${total.toLocaleString()} matching ${total === 1 ? 'entry' : 'entries'}`}
        {total > LIMIT && !loading && ` — showing ${offset + 1}–${Math.min(offset + LIMIT, total)}`}
      </div>

      <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
              <th style={th}>When</th><th style={th}>Who</th><th style={th}>Action</th>
              <th style={th}>Resource</th><th style={th}>IP</th><th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
                No entries match these filters in this window.
              </td></tr>
            )}
            {rows.map(r => (
              <React.Fragment key={r.id}>
                <tr style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmt(r.timestamp)}</td>
                  <td style={td}>
                    {r.user_email || r.user_id || <span style={{ color: '#9ca3af' }}>(system)</span>}
                    {r.user_type && <span style={{ color: '#9ca3af' }}> · {r.user_type}</span>}
                  </td>
                  <td style={{ ...td, fontWeight: 600, color: TONE(r.action) }}>
                    {r.action}
                    {r.status && r.status !== 'success' &&
                      <span style={{ marginLeft: 6, color: '#b91c1c' }}>({r.status})</span>}
                  </td>
                  <td style={td}>
                    {r.resource_type}{r.resource_id ? ` #${r.resource_id}` : ''}
                  </td>
                  <td style={{ ...td, color: '#6b7280', fontVariantNumeric: 'tabular-nums' }}>{r.ip_address || ''}</td>
                  <td style={td}>
                    {(r.details || r.error_message) && (
                      <button onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                              style={{ border: 'none', background: 'none', cursor: 'pointer',
                                       color: '#2563eb', fontSize: 13 }}>
                        {expanded === r.id ? 'hide' : 'details'}
                      </button>
                    )}
                  </td>
                </tr>
                {expanded === r.id && (
                  <tr><td colSpan={6} style={{ padding: '10px 14px', background: '#f9fafb' }}>
                    {r.error_message && (
                      <div style={{ color: '#991b1b', marginBottom: 6 }}>{r.error_message}</div>
                    )}
                    <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word', color: '#374151' }}>
                      {JSON.stringify(r.details, null, 2)}
                    </pre>
                    {r.request_path && (
                      <div style={{ marginTop: 6, color: '#6b7280', fontSize: 12 }}>
                        {r.request_method} {r.request_path}
                      </div>
                    )}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {total > LIMIT && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}
                  style={pageBtn(offset === 0)}>← Newer</button>
          <button disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)}
                  style={pageBtn(offset + LIMIT >= total)}>Older →</button>
        </div>
      )}
    </div>
  );
}

const th = { padding: '10px 14px', fontWeight: 600, color: '#374151', fontSize: 12,
             textTransform: 'uppercase', letterSpacing: '.04em' };
const td = { padding: '9px 14px', verticalAlign: 'top' };
const pageBtn = (disabled) => ({
  padding: '7px 14px', border: '1px solid #d1d5db', borderRadius: 6,
  background: disabled ? '#f3f4f6' : '#fff', color: disabled ? '#9ca3af' : '#111',
  cursor: disabled ? 'default' : 'pointer',
});
